import { isAxiosError } from 'axios';
import request from '../utils/axios/request';
import { PaginationContent } from '../utils/types/pagination';
import { Query } from '../utils/types/query';

export interface QueryClient extends Query {
	idClient?: number;
}

export interface ClientPermissions {
	isMonitoring: boolean;
	isMaintenance: boolean;
	isReport: boolean;
	isMovement: boolean;
}

export interface ClientSystemConfigs {
	moduloAuditoriaAtivado: boolean;
	processoFluxoUnicoAtivado: boolean;
	supplyReturnAtivado: boolean;
}

export interface IClient extends ClientSystemConfigs {
	id?: number;
	corporateName: string;
	fantasyName: string;
	cnpj: string;
	address: string;
	isActive: boolean;
	maxDailyMonitoringsAM1: string;
	maxDailyMonitoringsAM2: string;
	maxDailyMonitoringsPM1: string;
	maxDailyMonitoringsPM2: string;
	monitoringFee: string;
	maintenanceFee: string;
	permissions: ClientPermissions;
	system_configs?: ClientSystemConfigs;
	systemPropertie?: Partial<ClientSystemConfigs>;
	createdAt?: Date;
	updatedAt?: Date;
	actions?: any;
	energyCost?: number;
}

abstract class ClientService {
	static async getAll(params?: Query) {
		let isActive;
		if (params?.isActive && Number(params.isActive) > -1) {
			isActive = params.isActive;
		}
		try {
			const response: PaginationContent<IClient> = await request(
				'/clients',
				'get',
				{
					params: { ...params, isActive },
				}
			);
			return response;
		} catch (error: any) {}
	}

	static async create(
		data: IClient,
		setError: React.Dispatch<React.SetStateAction<string>>
	) {
		return this.createOrEdit(data, setError, 'post');
	}

	static async edit(
		data: IClient,
		setError: React.Dispatch<React.SetStateAction<string>>
	) {
		return this.createOrEdit(data, setError, 'patch');
	}

	private static async createOrEdit(
		data: IClient,
		setError: React.Dispatch<React.SetStateAction<string>>,
		mode: 'post' | 'patch'
	) {
		try {
			const {
				id,
				maxDailyMonitoringsAM1,
				maxDailyMonitoringsAM2,
				maxDailyMonitoringsPM1,
				maxDailyMonitoringsPM2,
				monitoringFee,
				maintenanceFee,
				createdAt,
				updatedAt,
				energyCost,
				system_configs,
				systemPropertie,
				...client
			} = data;
			const url = mode === 'post' ? '/clients' : `/clients/${id}`;
			const response = await request(url, mode, {
				data: {
					...client,
					maxDailyMonitoringsAM1: Number(maxDailyMonitoringsAM1),
					maxDailyMonitoringsPM1: Number(maxDailyMonitoringsPM1),
					maxDailyMonitoringsAM2: Number(maxDailyMonitoringsAM2),
					maxDailyMonitoringsPM2: Number(maxDailyMonitoringsPM2),
					monitoringFee: Number(monitoringFee),
					maintenanceFee: Number(maintenanceFee),
					cnpj: client.cnpj,
				},
			});
			return response;
		} catch (error) {
			if (isAxiosError(error) && error.status === 409)
				setError('alert.duplicated_email');
			else setError('alert.error.generic');
		}
	}

	static async delete(id: number) {
		const response = await request(`/clients/${id}`, 'delete');
		return response;
	}

	static async getEnergyCost(id: number) {
		try {
			const response: IClient = await request(
				`/clients/energy_cost/${id}`,
				'get'
			);
			return response.energyCost;
		} catch (error: any) {}
	}

	static async updateEnergyCost(id: number, energyCost: number) {
		try {
			const response = await request(
				`/clients/energy_cost/${id}`,
				'patch',
				{
					data: {
						energy_cost: Number(energyCost),
					},
				}
			);
			return response;
		} catch (error: any) {}
	}
}

export default ClientService;
